import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, Clock, FilePlus2, ListPlus, Timer } from 'lucide-react';
import CreateTaskModal from './CreateTaskModal';
import CreateTicketModal, { useCreateTicket } from './CreateTicketModal';
import { NewWorkLogModal } from './NewWorkLogModal';
import { globalQuickCreateForm, type GlobalForm } from './quickcreate/globalForm';
import QuickCreateFab from './quickcreate/QuickCreateFab';
import { SPEED_DIAL_ORDER } from './quickcreate/speedDialModel';
import { useQuickCreateStore, type QuickCreateAction, type QuickCreateItem } from './quickcreate/quickCreateStore';
import { useAuthStore } from '../store/authStore';
import { usePermissions } from '../hooks/usePermissions';
import { canUseTimer } from './timer/timerModel';
import { toast } from '../store/toastStore';
import type { Ticket } from '../types';

/**
 * QuickCreate (T-16a → T-32 → floating speed-dial, PRD §4.4,
 * DESIGN_SYSTEM.md §10.4): one short list that makes "new task" and "log time
 * on a task" one step from any screen.
 *
 * Owner decision 2026-10-06 (supersedes Q-020b): ONE floating "+" button at the
 * bottom inline-end corner on every breakpoint (`quickcreate/QuickCreateFab`).
 * It unfolds a speed-dial of pills upward. The top bar's "+ חדש" and the tab
 * bar's middle "+" are gone. Also: the `N` key, and the command palette lists
 * the same actions (`runQuickCreate`).
 *
 * Order (owner), from the button upward: משימה חדשה · רישום שעות על משימה ·
 * קריאה חדשה · התחלת טיימר · פגישה חדשה. Each item opens the EXISTING flow
 * (CreateTaskModal, NewWorkLogModal, CreateTicketModal, `ote:start-timer`,
 * `/meetings?create=true`). No new API. "משימה חדשה", "קריאה חדשה" and
 * "רישום שעות" open in place, over the current screen, EMPTY and unlocked,
 * whatever screen is open (D-283); context comes only from a screen's own
 * "הוספת משימה". Clocking in / out moved to the top bar
 * (owner, 2026-10-11).
 */

export { useQuickCreateStore, openQuickCreate, runQuickCreate } from './quickcreate/quickCreateStore';
export type { QuickCreateAction, QuickCreateItem } from './quickcreate/quickCreateStore';

/** The actions this user can take, in the owner's order (task and work log first). */
export function useQuickCreateItems(workLogHint?: string): QuickCreateItem[] {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const { canScreen, isOteEmployee } = usePermissions();
  if (!role) return [];
  // Same audiences as today's routes and controls: /tasks, /meetings and the
  // timer (admin, agent), ticket create (whoever has the tickets screen).
  // Logging hours is staff only: `POST /work-logs` is admin/agent, so a
  // customer manager (who may still read the work-log screen) never sees it.
  // An action that belongs to a screen shows only while that screen is open
  // (screen permissions); the timer is personal, not a screen. Clocking in and
  // out lives in the top bar (`attendance/AttendanceControl`, 2026-10-11).
  const staff = role === 'admin' || role === 'agent';
  const items: (QuickCreateItem | false)[] = [
    staff && canScreen('tasks') && { id: 'task', label: t('quickCreate.newTask'), icon: ListPlus },
    staff && canScreen('workLogs') && { id: 'worklog', label: t('quickCreate.logTime'), icon: Clock, hint: workLogHint },
    // P-45L (D-210): "קריאה חדשה" is for the customer side only; an employee has "משימה חדשה".
    !staff && canScreen('tickets') && { id: 'ticket', label: t('quickCreate.newTicket'), icon: FilePlus2 },
    // FIX-OPENREC — the timer follows the server's rule (any OTE employee), not the login claim.
    canUseTimer(role, isOteEmployee) && { id: 'timer', label: t('quickCreate.startTimer'), icon: Timer },
    staff && canScreen('meetings') && { id: 'meeting', label: t('shell.quickCreate.newMeeting'), icon: CalendarPlus },
  ];
  // One source for the order: the speed-dial model (bottom → top of the stack).
  return items
    .filter((i): i is QuickCreateItem => !!i)
    .sort((a, b) => SPEED_DIAL_ORDER.indexOf(a.id) - SPEED_DIAL_ORDER.indexOf(b.id));
}

// ─── Host: N key, the floating speed-dial, the forms ───────────────────────────

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName.toLowerCase();
  if (tag === 'textarea' || tag === 'select') return true;
  if (tag === 'input') {
    const type = (el as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(type);
  }
  return !!el.closest('[contenteditable="true"], [role="textbox"], [role="combobox"]');
}

/**
 * Routes whose own bottom bar the button would cover: the conversations
 * workspace is a full-height chat with its composer (send at the inline-end
 * corner). There the button stays hidden until `N` or the palette opens it.
 */
function concealFab(pathname: string): boolean {
  return pathname === '/discussions' || pathname.startsWith('/discussions/');
}

type OpenForm = GlobalForm | null;

/**
 * Mounted once in the app shell. Owns the floating button and its speed-dial,
 * the `N` shortcut and the forms the list opens. The forms remember what had
 * focus when they opened (the button) and return focus there when they close.
 */
export function QuickCreateHost() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { canPath } = usePermissions();
  const [form, setForm] = useState<OpenForm>(null);
  const formOpener = useRef<HTMLElement | null>(null);

  const items = useQuickCreateItems();
  const hasItems = items.length > 0;

  const run = useCallback((action: QuickCreateAction) => {
    useQuickCreateStore.getState().closeMenu();
    const active = document.activeElement as HTMLElement | null;
    formOpener.current = active && active !== document.body ? active : useQuickCreateStore.getState().anchor;
    switch (action) {
      case 'task':
      case 'worklog':
      case 'ticket':
        // D-283: global actions open empty and unlocked, whatever screen is open.
        setForm(globalQuickCreateForm(action));
        break;
      case 'timer':
        // GlobalTimer opens its existing start dialog on this event.
        window.dispatchEvent(new CustomEvent('ote:start-timer'));
        break;
      case 'meeting':
        // The meetings page opens its existing create dialog from this param.
        navigate('/meetings?create=true');
        break;
    }
  }, [navigate]);

  useEffect(() => {
    useQuickCreateStore.setState({ runner: run });
    return () => { if (useQuickCreateStore.getState().runner === run) useQuickCreateStore.setState({ runner: null }); };
  }, [run]);

  // Leaving the screen closes the list.
  useEffect(() => { useQuickCreateStore.getState().closeMenu(); }, [location.pathname]);

  // `N` opens the list from anywhere, except while typing or inside a dialog / menu.
  useEffect(() => {
    if (!hasItems) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
      // `code` keeps it working on a Hebrew layout, where the key types "מ".
      if (e.code !== 'KeyN' && e.key.toLowerCase() !== 'n') return;
      if (isTypingTarget(e.target) || isTypingTarget(document.activeElement)) return;
      if (document.querySelector('[aria-modal="true"], [role="dialog"], [role="alertdialog"], [role="menu"]')) return;
      const store = useQuickCreateStore.getState();
      if (store.open || form) return;
      e.preventDefault();
      store.openMenu();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [hasItems, form]);

  const closeForm = () => {
    setForm(null);
    const el = formOpener.current;
    requestAnimationFrame(() => { if (el?.isConnected) el.focus({ preventScroll: true }); });
  };

  return (
    <>
      {hasItems && <QuickCreateFab items={items} onRun={run} concealed={concealFab(location.pathname)} />}

      {form && createPortal(
        <>
          {form.kind === 'task' && (
            <CreateTaskModal
              placement={form.placement}
              lock={form.lock}
              onClose={closeForm}
              onSuccess={(ticketId, projectId, engagementId) => {
                // Same refresh as the tasks page, plus the project's / engagement's own list.
                queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
                if (engagementId) queryClient.invalidateQueries({ queryKey: ['engagement-tasks', engagementId] });
                queryClient.invalidateQueries({ queryKey: ['org-tickets'] });
                if (projectId) {
                  queryClient.invalidateQueries({ queryKey: ['project', projectId] });
                  queryClient.invalidateQueries({ queryKey: ['project-tickets', projectId] });
                }
                toast('success', t('quickCreate.taskCreated'), ticketId && canPath(`/tickets/${ticketId}`)
                  ? { action: { label: t('quickCreate.openTask'), onClick: () => navigate(`/tickets/${ticketId}`) } }
                  : undefined);
              }}
            />
          )}

          {form.kind === 'ticket' && (
            <QuickTicketForm
              projectId={form.projectId}
              organizationId={form.organizationId}
              engagementId={form.engagementId}
              onClose={closeForm}
              onCreated={(ticket) => {
                closeForm();
                toast('success', t('quickCreate.ticketCreated'), ticket?.id && canPath(`/tickets/${ticket.id}`)
                  ? { action: { label: t('quickCreate.openTicket'), onClick: () => navigate(`/tickets/${ticket.id}`) } }
                  : undefined);
              }}
            />
          )}

          {form.kind === 'worklog' && (
            <NewWorkLogModal
              initialTab={form.tab}
              initialItem={form.item}
              onClose={closeForm}
              onSuccess={() => {
                closeForm();
                toast('success', t('quickCreate.workLogCreated'), canPath('/work-logs') ? {
                  action: { label: t('quickCreate.viewWorkLogs'), onClick: () => navigate('/work-logs') },
                } : undefined);
              }}
            />
          )}
        </>,
        document.body,
      )}
    </>
  );
}

/** "קריאה חדשה" from QuickCreate: the tickets page's form and request, in a dialog over the current screen. */
function QuickTicketForm({ projectId, organizationId, engagementId, onClose, onCreated }: {
  projectId?: string;
  organizationId?: string;
  engagementId?: string;
  onClose: () => void;
  onCreated: (ticket: Ticket | undefined) => void;
}) {
  const create = useCreateTicket(onCreated);
  return (
    <CreateTicketModal
      initialProjectId={projectId}
      initialEngagementId={engagementId}
      organizationId={organizationId}
      onClose={onClose}
      onSubmit={(d) => create.mutate(d)}
      loading={create.isPending}
      error={create.error}
    />
  );
}
